import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Grid,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  AddBusiness as AddBusinessIcon,
  RemoveCircle as RemoveCircleIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';

interface Business {
  business_id: string;
  business_name: string;
  business_email: string;
}

interface TransferGroup {
  group_id: string;
  group_name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  business_count: number;
  businesses: Business[];
}

const BusinessTransferGroups: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<TransferGroup[]>([]);
  const [availableBusinesses, setAvailableBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addBusinessDialogOpen, setAddBusinessDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TransferGroup | null>(null);
  
  // Form state
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/business-transfer-groups`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setGroups(response.data);
    } catch (err: any) {
      console.error('Error fetching transfer groups:', err);
      setError(err.response?.data?.message || 'Failed to fetch transfer groups. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchAvailableBusinesses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/business-transfer-groups/available/businesses`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setAvailableBusinesses(response.data);
    } catch (err: any) {
      console.error('Error fetching businesses:', err);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
    fetchAvailableBusinesses();
  }, [fetchGroups, fetchAvailableBusinesses]);

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      await axios.post(
        `${API_BASE_URL}/business-transfer-groups`,
        {
          group_name: groupName.trim(),
          description: description.trim() || null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess('Transfer group created successfully');
      setCreateDialogOpen(false);
      setGroupName('');
      setDescription('');
      fetchGroups();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error creating transfer group:', err);
      setError(err.response?.data?.message || 'Failed to create transfer group. Please try again.');
    }
  };

  const handleEditGroup = async () => {
    if (!selectedGroup || !groupName.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      await axios.put(
        `${API_BASE_URL}/business-transfer-groups/${selectedGroup.group_id}`,
        {
          group_name: groupName.trim(),
          description: description.trim() || null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess('Transfer group updated successfully');
      setEditDialogOpen(false);
      setSelectedGroup(null);
      setGroupName('');
      setDescription('');
      fetchGroups();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error updating transfer group:', err);
      setError(err.response?.data?.message || 'Failed to update transfer group. Please try again.');
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;

    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      await axios.delete(
        `${API_BASE_URL}/business-transfer-groups/${selectedGroup.group_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess('Transfer group deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedGroup(null);
      fetchGroups();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting transfer group:', err);
      setError(err.response?.data?.message || 'Failed to delete transfer group. Please try again.');
    }
  };

  const handleAddBusiness = async () => {
    if (!selectedGroup || !selectedBusinessId) {
      setError('Please select a business');
      return;
    }

    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      await axios.post(
        `${API_BASE_URL}/business-transfer-groups/${selectedGroup.group_id}/businesses`,
        {
          business_id: selectedBusinessId
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess('Business added to transfer group successfully');
      setAddBusinessDialogOpen(false);
      setSelectedBusinessId('');
      fetchGroups();
      fetchAvailableBusinesses();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error adding business to group:', err);
      setError(err.response?.data?.message || 'Failed to add business to transfer group. Please try again.');
    }
  };

  const handleRemoveBusiness = async (businessId: string) => {
    if (!selectedGroup) return;

    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      await axios.delete(
        `${API_BASE_URL}/business-transfer-groups/${selectedGroup.group_id}/businesses/${businessId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSuccess('Business removed from transfer group successfully');
      setSelectedGroup(null);
      fetchGroups();
      fetchAvailableBusinesses();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error removing business from group:', err);
      setError(err.response?.data?.message || 'Failed to remove business from transfer group. Please try again.');
    }
  };

  const handleEditClick = (group: TransferGroup) => {
    setSelectedGroup(group);
    setGroupName(group.group_name);
    setDescription(group.description || '');
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (group: TransferGroup) => {
    setSelectedGroup(group);
    setDeleteDialogOpen(true);
  };

  const handleAddBusinessClick = (group: TransferGroup) => {
    setSelectedGroup(group);
    setAddBusinessDialogOpen(true);
    fetchAvailableBusinesses();
  };

  // Get businesses not already in the selected group
  const getAvailableBusinessesForGroup = () => {
    if (!selectedGroup) return availableBusinesses;
    const groupBusinessIds = new Set(selectedGroup.businesses.map(b => b.business_id));
    return availableBusinesses.filter(b => !groupBusinessIds.has(b.business_id));
  };

  if (loading && groups.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)', p: 3 }}> 
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Business Transfer Groups
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={() => {
              setGroupName('');
              setDescription('');
              setCreateDialogOpen(true);
            }}
          >
            Create Transfer Group
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {groups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Transfer Groups Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a transfer group to link businesses for asset transfers
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={() => {
                setGroupName('');
                setDescription('');
                setCreateDialogOpen(true);
              }}
            >
              Create Transfer Group
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {groups.map((group) => (
              <Grid item xs={12} md={6} key={group.group_id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {group.group_name}
                        </Typography>
                        {group.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {group.description}
                          </Typography>
                        )}
                        <Chip 
                          label={`${group.business_count} Business${group.business_count !== 1 ? 'es' : ''}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                      <Box>
                        <IconButton 
                          size="small" 
                          color="primary" 
                          onClick={() => handleEditClick(group)}
                          sx={{ mr: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => handleDeleteClick(group)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Businesses in Group:
                    </Typography>

                    {group.businesses.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No businesses in this group
                      </Typography>
                    ) : (
                      <List dense>
                        {group.businesses.map((business, index) => (
                          <React.Fragment key={business.business_id}>
                            <ListItem 
                              sx={{ px: 0 }}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setSelectedGroup(group);
                                    handleRemoveBusiness(business.business_id);
                                  }}
                                >
                                  <RemoveCircleIcon fontSize="small" />
                                </IconButton>
                              }
                            >
                              <ListItemText
                                primary={business.business_name}
                                secondary={business.business_email}
                              />
                            </ListItem>
                            {index < group.businesses.length - 1 && <Divider component="li" />}
                          </React.Fragment>
                        ))}
                      </List>
                    )}

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddBusinessIcon />}
                      onClick={() => handleAddBusinessClick(group)}
                      sx={{ mt: 2 }}
                      fullWidth
                    >
                      Add Business
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Create Group Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Transfer Group</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateGroup} variant="contained" color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Transfer Group</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditGroup} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Group Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Transfer Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the transfer group <strong>"{selectedGroup?.group_name}"</strong>?
            This will remove all business associations from this group. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteGroup} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Business Dialog */}
      <Dialog open={addBusinessDialogOpen} onClose={() => setAddBusinessDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Business to Transfer Group</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="business-select-label">Select Business</InputLabel>
            <Select
              labelId="business-select-label"
              label="Select Business"
              value={selectedBusinessId}
              onChange={(e) => setSelectedBusinessId(e.target.value)}
            >
              {getAvailableBusinessesForGroup().map((business) => (
                <MenuItem key={business.business_id} value={business.business_id}>
                  {business.business_name} ({business.business_email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {getAvailableBusinessesForGroup().length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              All businesses are already in this group
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBusinessDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddBusiness} 
            variant="contained" 
            color="primary"
            disabled={!selectedBusinessId}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BusinessTransferGroups;

